import axios from 'axios'
import {
  EDGE_CLIENT_VERSION_FILE,
  TOWER_VERSION_FILE,
  TRACKING_VERSION_FILE
} from '@/config/main'
import {
  Device,
  DeviceClientAction,
  DeviceLog,
  DeviceLogLevel,
  DeviceLogSourceType,
  DeviceMetricsDataPoint,
  DeviceSettings,
  DeviceSettingsPayload,
  TowerAction,
  TrackingAction,
  VlmAction
} from '@/types/device'
import { PaginatedResponse, WithListParams } from '@/types/pagination'
import { vpApi } from './vpApi'

type ClaimData = {
  device_name: string
  device_label: string
  claim_secret_key: string
}

const claimDevice = (data: ClaimData) => {
  return vpApi.post('/v1/devices/claiming/', data)
}

export const getDeviceList = async (params: WithListParams) => {
  const res = await vpApi.get<PaginatedResponse<Device[]>>(`/v1/devices/`, {
    params
  })

  return res.data
}

const getDeviceDetails = async (deviceId: string) => {
  const res = await vpApi.get<Device>(`/v1/devices/${deviceId}/`)
  return res.data
}

const getEdgeClientVersion = async () => {
  const res = await axios.get<string>(EDGE_CLIENT_VERSION_FILE, {
    responseType: 'text'
  })

  return res.data
}

const getTowerVersion = async () => {
  if (!TOWER_VERSION_FILE) {
    return ''
  }

  const res = await axios.get<string>(TOWER_VERSION_FILE, {
    responseType: 'text'
  })

  return res.data
}

type TriggerEdgeClientActionParams = {
  deviceId: string
  action: 'restart' | 'update'
}

type TriggerEdgeClientResponse = {
  task_id: string
  log_id: string
}

const triggerEdgeClientAction = async ({
  deviceId,
  action
}: TriggerEdgeClientActionParams) => {
  const res = await vpApi.put<TriggerEdgeClientResponse>(
    `/v1/devices/${deviceId}/client/`,
    {
      action
    }
  )

  return res.data
}

const getLatestClientAction = async (deviceId: string, logId?: string) => {
  const res = await vpApi.get<DeviceClientAction | null>(
    `/v1/devices/${deviceId}/client/actions/latest/`,
    { params: logId ? { log_id: logId } : undefined }
  )

  return res.data
}

type TriggerVlmServiceActionParams = {
  deviceId: string
  action: VlmAction
}

const triggerVlmServiceAction = async ({
  deviceId,
  action
}: TriggerVlmServiceActionParams) => {
  await vpApi.post(`/v1/devices/${deviceId}/vlm-service/`, { action })
}

type TriggerTowerServiceActionParams = {
  deviceId: string
  action: TowerAction
  version?: string
}

const triggerTowerServiceAction = async ({
  deviceId,
  action,
  version
}: TriggerTowerServiceActionParams) => {
  await vpApi.post(`/v1/devices/${deviceId}/tower-service/`, {
    action,
    ...(version ? { version } : {})
  })
}

type TriggerTrackingServiceActionParams = {
  deviceId: string
  action: TrackingAction
  version?: string
}

const triggerTrackingServiceAction = async ({
  deviceId,
  action,
  version
}: TriggerTrackingServiceActionParams) => {
  await vpApi.post(`/v1/devices/${deviceId}/tracking-service/`, {
    action,
    ...(version ? { version } : {})
  })
}

const getTrackingVersion = async () => {
  if (!TRACKING_VERSION_FILE) {
    return ''
  }

  const res = await axios.get<string>(TRACKING_VERSION_FILE, {
    responseType: 'text'
  })

  return res.data
}

const getDeviceSettings = async (deviceId: string) => {
  const res = await vpApi.get<DeviceSettings>(
    `/v1/devices/${deviceId}/vlm-settings/`
  )
  return res.data
}

const createDeviceSettings = async ({
  deviceId,
  payload
}: {
  deviceId: string
  payload: DeviceSettingsPayload
}) => {
  const res = await vpApi.post<DeviceSettings>(
    `/v1/devices/${deviceId}/vlm-settings/`,
    payload
  )
  return res.data
}

const updateDeviceSettings = async ({
  deviceId,
  payload
}: {
  deviceId: string
  payload: Partial<DeviceSettingsPayload>
}) => {
  const res = await vpApi.patch<DeviceSettings>(
    `/v1/devices/${deviceId}/vlm-settings/`,
    payload
  )
  return res.data
}

type PingDeviceResponse = {
  task_id: string
  device_id: string
}

type DeviceMetricsResponse = Record<string, DeviceMetricsDataPoint[]>

export type GetDeviceMetricsParams = {
  keys: string
  startTs?: number
  endTs?: number
  agg?: 'AVG' | 'MIN' | 'MAX' | 'SUM' | 'COUNT' | 'NONE'
  interval?: number
  limit?: number
}

export type GetDeviceLogsParams = WithListParams<{
  device_id?: string
  source_type?: DeviceLogSourceType
  group_id?: string
  deployment_id?: string
  camera_id?: string
  app_id?: string
  level?: DeviceLogLevel
  log_tag?: string
  start_date?: string
  end_date?: string
  search?: string
}>

const getDeviceMetrics = async (
  deviceId: string,
  params: GetDeviceMetricsParams
) => {
  const res = await vpApi.get<DeviceMetricsResponse>(
    `/v1/devices/${deviceId}/metrics/`,
    { params }
  )
  return res.data
}

const getDeviceLogs = async (params: GetDeviceLogsParams) => {
  const res = await vpApi.get<PaginatedResponse<DeviceLog[]>>(
    '/v1/devices/logs/',
    { params }
  )
  return res.data
}

const pingDevice = async (deviceId: string) => {
  const res = await vpApi.put<PingDeviceResponse>(
    `/v1/devices/${deviceId}/ping-device/`
  )
  return res.data
}

export const deviceApi = {
  claimDevice,
  getDeviceList,
  getDeviceDetails,
  getEdgeClientVersion,
  getTowerVersion,
  triggerEdgeClientAction,
  getLatestClientAction,
  triggerVlmServiceAction,
  triggerTowerServiceAction,
  triggerTrackingServiceAction,
  getTrackingVersion,
  pingDevice,
  getDeviceMetrics,
  getDeviceSettings,
  createDeviceSettings,
  updateDeviceSettings,
  getDeviceLogs
}
